/**
 * The parent / variant / single structure an import will write, worked out
 * from the preview rows before anything is committed.
 *
 * Two things make a group:
 * - the STORE (native): a Shopify product with 2+ variants, or an expanded
 *   WooCommerce / Square Online option set — `variantGroupOf`. Its parent is a
 *   synthetic product-level doc that is not itself a row.
 * - the source's grouping RULE, once the curator accepts a proposed group in
 *   the preview — `resolveAcceptedGroups`. Its parent is one of the rows.
 *
 * Everything else is a single: a top-level reference with no variants.
 *
 * Only ticked rows count, mirroring the import (unticked rows are never
 * written, so they are no part of the structure). Pure, so the preview, the
 * source card's Test and the summary all count the same way.
 */
import { variantGroupOf, type StoreIdentifiedEntry } from './storeDocIds';
import type { ResolvedRuleGroup } from './variantGroupingRule';

export type RowRole = 'store-variant' | 'rule-parent' | 'rule-variant' | 'single' | 'skipped';

export interface StructureRow {
  entry: StoreIdentifiedEntry;
  included: boolean;
}

export interface StoreGroup {
  key: string;
  /** The synthetic parent doc the import upserts for this product. */
  parentDocId: string;
  /** Ticked member rows, in row order. */
  members: number[];
}

export interface ImportStructure {
  /** One role per row (unticked rows are 'skipped'). */
  roles: RowRole[];
  storeGroups: StoreGroup[];
  ruleGroups: ResolvedRuleGroup[];
  counts: StructureCounts;
}

export interface StructureCounts {
  /** storeParents + ruleParents. */
  parents: number;
  /** Synthetic product-level parents (not rows). */
  storeParents: number;
  /** Rows written as the parent of an accepted rule group. */
  ruleParents: number;
  variants: number;
  singles: number;
}

/**
 * Lay out the structure. `ruleGroups` are the ACCEPTED groups; members that are
 * unticked, or that the store already groups, are dropped, and a rule group
 * left without a ticked parent and at least one ticked variant does not form.
 */
export function planImportStructure(
  rows: readonly StructureRow[],
  ruleGroups: readonly ResolvedRuleGroup[] = [],
): ImportStructure {
  const roles: RowRole[] = rows.map((r) => (r.included ? 'single' : 'skipped'));

  const byKey = new Map<string, StoreGroup>();
  rows.forEach((row, i) => {
    if (!row.included) return;
    const g = variantGroupOf(row.entry);
    if (!g) return;
    const group = byKey.get(g.key) ?? { key: g.key, parentDocId: g.parentDocId, members: [] };
    group.members.push(i);
    byKey.set(g.key, group);
    roles[i] = 'store-variant';
  });
  const storeGroups = [...byKey.values()];

  const formed: ResolvedRuleGroup[] = [];
  for (const g of ruleGroups) {
    if (roles[g.parentIndex] !== 'single') continue;
    const variantIndices = g.variantIndices.filter((i) => roles[i] === 'single');
    if (variantIndices.length === 0) continue;
    roles[g.parentIndex] = 'rule-parent';
    for (const i of variantIndices) roles[i] = 'rule-variant';
    formed.push({ ...g, variantIndices });
  }

  const count = (role: RowRole) => roles.filter((r) => r === role).length;
  const ruleParents = count('rule-parent');
  return {
    roles,
    storeGroups,
    ruleGroups: formed,
    counts: {
      parents: storeGroups.length + ruleParents,
      storeParents: storeGroups.length,
      ruleParents,
      variants: count('store-variant') + count('rule-variant'),
      singles: count('single'),
    },
  };
}

/**
 * True when at least two rows are being imported and the store groups none of
 * them: every product came back top-level with no variants. Such a source can
 * only be grouped by a rule (KNIS Kyoto lists KN001-MT, KN001-MTRG, … as
 * separate one-variant products).
 */
export function lacksNativeGrouping(rows: readonly StructureRow[]): boolean {
  const ticked = rows.filter((r) => r.included);
  return ticked.length >= 2 && !ticked.some((r) => variantGroupOf(r.entry));
}

export interface CreateUpdateSplit {
  wouldCreate: number;
  wouldUpdate: number;
}

/**
 * How many docs would be created vs already exist, over every doc the import
 * writes: each non-skipped row's doc id plus each store group's synthetic
 * parent. `docIdOf` returns the row's store composite id, or undefined for a
 * row matched by its reference string instead — those rows are left out and
 * reported in `unmatched`, since only the import's full brand read can place
 * them.
 */
export function splitCreateUpdate(
  rows: readonly StructureRow[],
  structure: ImportStructure,
  docIdOf: (entry: StoreIdentifiedEntry) => string | undefined,
  existingDocIds: ReadonlySet<string>,
): CreateUpdateSplit & { unmatched: number; parents: CreateUpdateSplit; variants: CreateUpdateSplit; singles: CreateUpdateSplit } {
  const split = () => ({ wouldCreate: 0, wouldUpdate: 0 });
  const parents = split();
  const variants = split();
  const singles = split();
  let unmatched = 0;
  const tally = (bucket: CreateUpdateSplit, exists: boolean) => {
    if (exists) bucket.wouldUpdate += 1;
    else bucket.wouldCreate += 1;
  };
  for (const g of structure.storeGroups) tally(parents, existingDocIds.has(g.parentDocId));
  rows.forEach((row, i) => {
    const role = structure.roles[i];
    if (role === 'skipped') return;
    const id = docIdOf(row.entry);
    if (!id) {
      unmatched += 1;
      return;
    }
    const bucket = role === 'rule-parent' ? parents : role === 'single' ? singles : variants;
    tally(bucket, existingDocIds.has(id));
  });
  return {
    wouldCreate: parents.wouldCreate + variants.wouldCreate + singles.wouldCreate,
    wouldUpdate: parents.wouldUpdate + variants.wouldUpdate + singles.wouldUpdate,
    unmatched,
    parents,
    variants,
    singles,
  };
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** "3 parents · 7 variants · 2 singles" — zero parts are left out. */
export function describeCounts(c: { parents: number; variants: number; singles: number }): string {
  const parts = [
    c.parents > 0 && plural(c.parents, 'parent'),
    c.variants > 0 && plural(c.variants, 'variant'),
    c.singles > 0 && plural(c.singles, 'single'),
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : 'nothing';
}

/** The hint shown when a source returns no native parent-child groups. */
export const NO_NATIVE_GROUPING_HINT =
  "This source doesn't use native parent-child relationships — every product came back on its own, with no variants. " +
  'If some of these are versions of the same watch, set a grouping rule on the source (e.g. group references that share their first 5 characters, like KN001).';

/**
 * The preview's display order: each group's rows together (members in the
 * order given — a rule group passes its parent first), placed where the group's
 * first row was; ungrouped rows keep their place. Returns absolute row indices,
 * each exactly once, plus the group key of every grouped row.
 */
export function groupedDisplayOrder(
  rowCount: number,
  groups: ReadonlyArray<{ key: string; members: readonly number[] }>,
): { order: number[]; keyByIndex: Map<number, string>; membersByKey: Map<string, number[]> } {
  const keyByIndex = new Map<number, string>();
  const membersByKey = new Map<string, number[]>();
  for (const g of groups) {
    const members = g.members.filter((m) => m >= 0 && m < rowCount && !keyByIndex.has(m));
    if (members.length === 0) continue;
    for (const m of members) keyByIndex.set(m, g.key);
    membersByKey.set(g.key, members);
  }
  const order: number[] = [];
  const placed = new Set<string>();
  for (let idx = 0; idx < rowCount; idx++) {
    const key = keyByIndex.get(idx);
    if (!key) order.push(idx);
    else if (!placed.has(key)) {
      placed.add(key);
      order.push(...membersByKey.get(key)!);
    }
  }
  return { order, keyByIndex, membersByKey };
}
